import { Form, FormikProps } from "formik"
import { Formik } from "../../../../shared/components/forms/SchemaFormik"
import { isPossiblePhoneNumber } from "libphonenumber-js"
import { forwardRef, Ref, useMemo } from "react"
import * as Yup from "yup"

import { useAppLocalization } from "../../../localization/components/AppLocalizationProvider"
import { Admin, History } from "../../userTypes"

import { AdministratorFormValues, defaultAdministratorFormValues } from "../administratorFormTypes"
import { AdministratorFormContent } from "./AdministratorFormContent"

type AdministratorFormProps = Readonly<{
    /** Only decides which roles are offered and whether the PIN field can show — never which endpoint is called. */
    adminType: Admin["type"]
    isEditMode: boolean
    isOwnProfile?: boolean
    initialValues?: AdministratorFormValues
    history?: History
    onSubmit: (values: AdministratorFormValues) => void
}>

type FormikRef = Ref<FormikProps<AdministratorFormValues>>

export const AdministratorForm = forwardRef(
    (
        { adminType, isEditMode, isOwnProfile, initialValues, history, onSubmit }: AdministratorFormProps,
        ref: FormikRef,
    ) => {
        const { l10n } = useAppLocalization()

        const validationSchema = useMemo(
            () =>
                Yup.object().shape({
                    username: Yup.string(),
                    displayName: Yup.string().required(
                        l10n.getString("office-manager-create-user-required-field-empty"),
                    ),
                    email: Yup.string()
                        .email(l10n.getString("office-manager-create-user-invalid-email-address"))
                        .required(l10n.getString("office-manager-create-user-required-field-empty")),
                    mobileNumber: Yup.string().test({
                        name: "is-phone-number",
                        test: (value) => (value ? isPossiblePhoneNumber(value, "DK") : true),
                        message: l10n.getString("office-manager-create-user-invalid-phone-number"),
                    }),
                    allowToCreateAdmins: Yup.boolean(),
                    restrictAccessToIpAddresses: Yup.string(),
                    pinCode: Yup.string().matches(/^\d{4}$/, {
                        message: l10n.getString("office-manager-security-pin-invalid"),
                        excludeEmptyString: true,
                    }),
                    isActive: Yup.boolean(),
                    resendInvitation: Yup.boolean(),
                    roles: Yup.array().of(Yup.string()),
                }),
            [l10n],
        )

        return (
            <Formik<AdministratorFormValues>
                enableReinitialize
                initialValues={initialValues ?? defaultAdministratorFormValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                innerRef={ref}
            >
                <Form>
                    <AdministratorFormContent
                        adminType={adminType}
                        isEditMode={isEditMode}
                        isOwnProfile={isOwnProfile}
                        history={history}
                    />
                </Form>
            </Formik>
        )
    },
)
